import { useMutation } from "@apollo/client/react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import { Formik } from "formik"
import { X } from "lucide-mui"
import React, { useState, useEffect } from "react"

import { forgotPasswordSchema } from "@/components/auth/validation_schemas"
import { TextField } from "@/components/form"
import { RequestPasswordResetDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

const STYLES = {
  dialogTitle: "flex items-center justify-between gap-2 pr-2",
  description: "text-sm text-gray-600 mb-4",
  fieldsContainer: "flex flex-col gap-4",
  errorText: "text-sm text-red-600",
  successContainer: "flex flex-col gap-2 py-2",
  successTitle: "text-lg font-semibold",
  successDescription: "text-sm text-gray-600",
} as const

type ForgotPasswordModalProps = {
  open: boolean
  onClose: () => void
}

type FormValues = {
  email: string
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ open, onClose }) => {
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")
  const [requestPasswordReset] = useMutation(RequestPasswordResetDocument)

  useEffect(() => {
    if (!open) {
      setSubmitted(false)
      setError("")
    }
  }, [open])

  const handleSubmit = async (values: FormValues, { setSubmitting }: { setSubmitting: (v: boolean) => void }) => {
    setError("")

    try {
      const { data } = await requestPasswordReset({ variables: { email: values.email } })

      if (data?.publicRequestPasswordReset?.success) {
        setSubmitted(true)
      } else {
        setError(i18n.t("app.auth.forgot_password.generic_error"))
      }
    } catch (err) {
      showErrorToast()
      console.error(err)
      setError(i18n.t("app.auth.forgot_password.generic_error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-testid="auth-forgot-password-dialog">
      <DialogTitle className={STYLES.dialogTitle}>
        <span>
          {submitted ? i18n.t("app.auth.forgot_password.success_title") : i18n.t("app.auth.forgot_password.title")}
        </span>
        <IconButton size="small" onClick={onClose} data-testid="auth-forgot-password-close-btn">
          <X fontSize="small" />
        </IconButton>
      </DialogTitle>

      {submitted ? (
        <>
          <DialogContent>
            <div className={STYLES.successContainer} data-testid="auth-forgot-password-success">
              <div className={STYLES.successDescription}>{i18n.t("app.auth.forgot_password.success_description")}</div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="contained" data-testid="auth-forgot-password-done-btn">
              {i18n.t("app.auth.forgot_password.close")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <Formik
          initialValues={{ email: "" }}
          validationSchema={forgotPasswordSchema}
          onSubmit={handleSubmit}
          validateAfterSubmit={true}
        >
          {({ isSubmitting, handleSubmit: onFormSubmit }) => (
            <form onSubmit={onFormSubmit} data-testid="auth-forgot-password-form">
              <DialogContent>
                <p className={STYLES.description}>{i18n.t("app.auth.forgot_password.description")}</p>
                <div className={STYLES.fieldsContainer}>
                  <TextField
                    name="email"
                    label={i18n.t("app.auth.forgot_password.email")}
                    type="email"
                    data-testid="auth-forgot-password-email-input"
                  />

                  {!!error && (
                    <div className={STYLES.errorText} data-testid="auth-forgot-password-error">
                      {error}
                    </div>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} data-testid="auth-forgot-password-cancel-btn">
                  {i18n.t("app.auth.forgot_password.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  data-testid="auth-forgot-password-submit-btn"
                >
                  {isSubmitting ? "..." : i18n.t("app.auth.forgot_password.submit")}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      )}
    </Dialog>
  )
}

export default ForgotPasswordModal
